import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import { editInstruction } from "services/doubtfulService";
import { fetchAdminUnitDetail } from "services/organizationService";
import { DoubtfulCase } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  isEdit?: boolean;
  instructuion: DoubtfulCase;
  onClose: (refetch: boolean) => void;
};

const OrgUnitForm: FC<Props> = ({ isEdit = false, instructuion, onClose }) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const [lowerOrgId, setLowerOrgId] = useState("");
  const [adminUnitDetail, setAdminUnitDetail] = useState<any>();

  useEffect(() => {
    if (loginData) {
      fetchAdminUnitDetail(loginData?.admin_unit).then((data) => {
        if (data) {
          setAdminUnitDetail(data);
        }
      });
    }
  }, [loginData]);

  const validateForm = () => {
    if (!lowerOrgId) return t("org_unit_is_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    editInstruction({
      id: instructuion?.id,
      org_id: lowerOrgId,
    })
      .then(() => {
        showTooltip(
          isEdit ? t("successfully_updated") : t("successfully_submitted"),
          "success",
        );
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };

  return (
    <div className="w-full bg-white rounded-xl px-6 py-4 shadow">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {t("org_unit")}
        </h2>
        <p className="text-sm text-gray-600">{t("add_organizational_unit")}</p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t("org_unit")} *
            </label>
            <select
              value={lowerOrgId}
              onChange={(e) => {
                setLowerOrgId(e.target.value);
              }}
              className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            >
              <option value="">{t("select_org_unit")}</option>
              {(adminUnitDetail?.child_org_units?.units ?? []).map(
                (unit: { uuid: string; unit_name: string }) => (
                  <option key={unit?.uuid} value={unit?.uuid}>
                    {unit?.unit_name}
                  </option>
                ),
              )}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              onClose(false);
            }}
          >
            {t("cancel")}
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center shadow-sm hover:shadow"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="animate-spin">{LoadingIcon}</span>
                {t("processing")}
              </>
            ) : (
              t("submit")
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrgUnitForm;
